/**
 * @fileoverview Molecule: one habit, its schedule and its goal progress.
 * @module components/molecules/HabitCard
 */

import { Button, Icon, ProgressBar } from "@/components/atoms"
import { describeSchedule } from "@/lib/domain/schedule"
import { computeGoalProgress, formatRatio } from "@/lib/domain/stats"
import type { Habit, HabitId } from "@/types/types"

import "./HabitCard.css"

/** Props accepted by {@link HabitCard}. */
export interface HabitCardProps {
	/** The habit to display. */
	readonly habit: Habit
	/** Repetitions already logged today for this habit. */
	readonly todayReps: number
	/** True when the habit's schedule is due today. */
	readonly isDueToday: boolean
	/** Current streak, in consecutive due dates completed. */
	readonly streak: number
	/** Opens the full detail, edit and delete modal. */
	readonly onOpen: (id: HabitId) => void
	/** Logs repetitions for today; a negative delta removes some. */
	readonly onLogReps: (id: HabitId, delta: number) => void
}

/**
 * Compact habit card, sized for a vertical list.
 *
 * @description
 * The head, schedule, progress and figures sit inside a single button that
 * opens {@link HabitDetailsModal}; the rep-logging controls stay outside it
 * so the card never nests a button inside a button. The "−1" control only
 * appears once today has a rep logged to retract.
 *
 * @param props - The habit and the actions available on it.
 * @returns The rendered card.
 */
export function HabitCard({
	habit,
	todayReps,
	isDueToday,
	streak,
	onOpen,
	onLogReps,
}: HabitCardProps) {
	const goal = computeGoalProgress(habit)

	return (
		<li className={`habit${isDueToday ? " habit--due" : ""}`}>
			<button
				type="button"
				className="habit__open"
				onClick={() => {
					onOpen(habit.id)
				}}
				aria-label={`Voir le détail de ${habit.name}`}
			>
				<div className="habit__head">
					<div className="habit__title-group">
						<h3 className="habit__name">{habit.name}</h3>
						<span className="habit__schedule">
							{describeSchedule(habit.schedule)}
						</span>
					</div>
					{streak > 0 && (
						<span className="habit__streak">
							<Icon name="flame" size={16} />
							{streak}
						</span>
					)}
				</div>

				<ProgressBar
					ratio={goal.ratio}
					label={`Objectif de ${habit.name}`}
				/>

				<div className="habit__figures">
					<span className="habit__figure">
						<strong>
							{habit.totalReps} / {habit.goalReps}
						</strong>{" "}
						répétitions
					</span>
					<span className="habit__figure">
						{goal.isReached ? (
							<span className="habit__done">
								🟢 Objectif atteint
							</span>
						) : (
							<strong>{formatRatio(goal.ratio)}</strong>
						)}
					</span>
				</div>
			</button>

			<div className="habit__actions">
				{isDueToday && (
					<span className="habit__due-badge">Aujourd’hui</span>
				)}
				<Button
					size="sm"
					onClick={() => {
						onLogReps(habit.id, 1)
					}}
				>
					+1
				</Button>
				{todayReps > 0 && (
					<Button
						size="sm"
						variant="ghost"
						iconOnly
						onClick={() => {
							onLogReps(habit.id, -1)
						}}
						ariaLabel={`Retirer une répétition de ${habit.name} aujourd’hui`}
					>
						<Icon name="minus" size={16} />
					</Button>
				)}
			</div>
		</li>
	)
}
